import React, { useCallback, useRef } from 'react';
import { useModal } from 'mui-modal-provider';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import {
  AppointmentRequestButton as AppointmentRequestButtonView,
} from './appointment-request-button';
import { AppointmentRegistrationModal } from '../appointment-registration-modal';
import { AskLoginModal } from '../../../common/ask-login-modal';
import { useGoBack } from '../../../../hooks/use-go-back';
import { InternalLinks } from '../../../../constants';
import { setRedirectUrl } from '../../../../utils/redirect/redirect';
import { GET_IS_USER_LOGGED_IN } from '../../../../graphql/user/queries';
import {
  IConsumerAppointments,
} from '../../../../graphql/appointments/models/get-appointments.models';
import { GET_APPOINTMENTS } from '../../../../graphql/appointments/queries/get-appointments';
import { AppointmentRequestProps } from './appointment-request-button.models';
import { EcpServices } from '../ecp-locator';
import { CvRegistrationSuccess } from '../cv-modals/cv-registration-success';
import { isAppointmentContentShow } from '../../../../utils/appointment/is-appoinment-content-show';
import { pushEcpLocatorEvent } from '../../../../utils/analytics/push-ecp-locator-event';
import { EcpLocatorEvent } from '../../../../models/ya-metrica-events';

export const AppointmentRequestButton = ({
  appointmentUrl, isRetailer, storeCode, ecpService, testId, refetchProfile,
}: AppointmentRequestProps): JSX.Element => {
  const { data: { user: { isLoggedIn } } } = useQuery(GET_IS_USER_LOGGED_IN);
  const { data, loading, error, refetch } = useQuery<IConsumerAppointments>(GET_APPOINTMENTS, { skip: !isLoggedIn });

  const { showModal } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPreviousLocationState } = useGoBack();
  const fittingCompleted = useRef(false);

  const appointments = data?.allAppointmentsByAuthId?.edges || [];
  const firstAppointment = appointments[0];
  const hasAppointment = isAppointmentContentShow(
    firstAppointment?.node?.date, firstAppointment?.node?.status,
  );
  const activeAppointment = hasAppointment ? firstAppointment : undefined;
  const externalAppointmentUrl = isLoggedIn && !activeAppointment ? appointmentUrl : undefined;

  const onNavigate = (url: string): void => {
    navigate(url, { state: hasPreviousLocationState });
  };

  const pushStoreCode = useCallback(() => {
    if (fittingCompleted.current) {
      fittingCompleted.current = false;
      return;
    }

    if (storeCode) {
      pushEcpLocatorEvent(EcpLocatorEvent.LensFitting, storeCode);
    }
  }, [storeCode]);

  const pushFittingEvent = useCallback((eventName: EcpLocatorEvent) => {
    if (storeCode) {
      pushEcpLocatorEvent(eventName, storeCode);
    }
  }, [storeCode]);

  const onRegistrationComplete = useCallback(() => {
    fittingCompleted.current = true;

    if (ecpService === EcpServices.cv) {
      showModal(CvRegistrationSuccess, { onNavigate: () => onNavigate(InternalLinks.appointment) });
    } else {
      onNavigate(InternalLinks.appointment);
    }
  }, [ecpService, showModal, onNavigate]);

  const handleNavigateToRegistration = useCallback(() => {
    setRedirectUrl(location.pathname);
    navigate(InternalLinks.registration);
  }, [navigate, location.pathname]);

  const handleNavigateToLogin = useCallback(() => {
    setRedirectUrl(location.pathname);
    navigate(InternalLinks.login);
  }, [navigate, location.pathname]);

  const handleButtonClick = () => {
    if (!hasAppointment) {
      pushStoreCode();
    }

    if (isLoggedIn) {
      if (isRetailer) {
        onNavigate(`${InternalLinks.ecpLocator}?ecpService=fit`);
      } else {
        showModal(AppointmentRegistrationModal, {
          updateAppointments: (): void => {
            refetch();
            refetchProfile?.();
          },
          onRegistrationComplete,
          ecpService,
          storeCode,
          onSelectDateForFitting: () => pushFittingEvent(EcpLocatorEvent.SelectDateForFitting),
          onChangeFittingDate: () => pushFittingEvent(EcpLocatorEvent.ChangeFittingDate),
          onRequestFitting: () => pushFittingEvent(EcpLocatorEvent.RequestFitting),
          onReviewFittingRequest: () => pushFittingEvent(EcpLocatorEvent.ReviewFittingRequest),
          goNextButtonTestIdsByStep: {
            appointmentDate: 'ecp-request-stepper-next-button', // Used by YM
            appointmentCheck: 'ecp-request-stepper-confirm-button', // Used by YM
            appointmentSuccess: 'ecp-request-stepper-view-button', // Used by YM
          },
        });
      }
    } else {
      showModal(AskLoginModal, {
        onNavigateToRegistration: handleNavigateToRegistration,
        onNavigateToLogin: handleNavigateToLogin,
      });
    }
  };

  return (
    <AppointmentRequestButtonView
      isLoggedIn={isLoggedIn}
      appointmentUrl={externalAppointmentUrl}
      isLoading={loading}
      isError={!!error}
      onClick={handleButtonClick}
      testId={testId}
      ecpService={ecpService}
      isRequestButtonDisabled={hasAppointment}
      appointmentType={firstAppointment?.node?.appointmentType}
    />
  );
};
